import React from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { LeadsLayout } from '@/components/LeadsLayout';
import { FileText } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Auth header helper
const getAuthHeader = () => {
  const token = localStorage.getItem('access_token') || localStorage.getItem('token');
  return {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  };
};

// Stage colors for display
const STAGE_COLORS = {
  new: 'bg-blue-100 text-blue-700',
  contacted: 'bg-purple-100 text-purple-700',
  interested: 'bg-green-100 text-green-700',
  test_drive: 'bg-yellow-100 text-yellow-700',
  negotiation: 'bg-orange-100 text-orange-700',
  finance_pending: 'bg-pink-100 text-pink-700',
  dp_pending: 'bg-indigo-100 text-indigo-700',
  won: 'bg-emerald-100 text-emerald-700',
  lost: 'bg-red-100 text-red-700'
};

const LEAD_SOURCES = [
  'Walk-in', 
  'Phone Enquiry', 
  'Website', 
  'Digital Marketing',
  'Referral', 
  'Exhibition',
  'Campaign Activity',
  'Exchange Mela',
  'Incoming Calls',
  'Outdoor Advertising',
  'TV Advertising',
  'Paper Advertising',
  'Radio Advertising',
  'Online Portal', 
  'Other'
];

const PRIORITIES = [
  { value: 'hot', label: '🔥 Hot', color: 'bg-red-500' },
  { value: 'warm', label: '🌡️ Warm', color: 'bg-yellow-500' },
  { value: 'cold', label: '❄️ Cold', color: 'bg-blue-500' }
];

const STAGES = [
  { value: 'new', label: 'New Lead' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'interested', label: 'Interested' },
  { value: 'test_drive', label: 'Test Drive' },
  { value: 'negotiation', label: 'Negotiation' },
  { value: 'finance_pending', label: 'Finance Approval' },
  { value: 'dp_pending', label: 'DP Pending' },
  { value: 'won', label: 'Won' },
  { value: 'lost', label: 'Lost' }
];

const FINANCE_STATUS_OPTIONS = [
  { value: 'login', label: 'Login' },
  { value: 'fi_pending', label: 'FI Pending' },
  { value: 'bank_statement_pending', label: 'Bank Statement Pending' },
  { value: 'clarification_pending', label: 'Clarification Pending' },
  { value: 'enach_pending', label: 'E-Nach Pending' },
  { value: 'ipr_invoice_pending', label: 'IPR/Invoice Pending' },
  { value: 'disbursement_pending', label: 'Disbursement Pending' },
  { value: 'disbursement_done', label: 'Disbursement Done' },
  { value: 'rejected', label: 'Rejected' }
];

const SALUTATIONS = ['Mr.', 'Mrs.', 'Ms.', 'Dr.'];

function LeadEntryForm({ user, onLogout, mode = 'create' }) {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  
  // Duplicate check state
  const [showDuplicateModal, setShowDuplicateModal] = React.useState(false);
  const [duplicateLeads, setDuplicateLeads] = React.useState([]);
  const [checkingDuplicate, setCheckingDuplicate] = React.useState(false);
  const [duplicateChecked, setDuplicateChecked] = React.useState(false);
  const [proceedWithDuplicate, setProceedWithDuplicate] = React.useState(false);
  
  // Masters data from Sales module
  const [brands, setBrands] = React.useState([]);
  const [allModels, setAllModels] = React.useState([]); // All models for lookup
  const [allVariants, setAllVariants] = React.useState([]); // All variants for lookup
  const [allColors, setAllColors] = React.useState([]); // All colors for lookup
  
  // Filtered cascading data for interested vehicle
  const [filteredModels, setFilteredModels] = React.useState([]);
  const [filteredVariants, setFilteredVariants] = React.useState([]);
  const [filteredColors, setFilteredColors] = React.useState([]);
  
  // Filtered cascading data for exchange vehicle
  const [exchangeFilteredModels, setExchangeFilteredModels] = React.useState([]);
  const [exchangeFilteredVariants, setExchangeFilteredVariants] = React.useState([]);
  const [exchangeFilteredColors, setExchangeFilteredColors] = React.useState([]);
  
  const [salesReps, setSalesReps] = React.useState([]);
  const [financiers, setFinanciers] = React.useState([]);
  const [leadSources, setLeadSources] = React.useState([]);
  const [subSources, setSubSources] = React.useState([]);
  
  // Form data
  const [formData, setFormData] = React.useState({
    salutation: '',
    first_name: '',
    last_name: '',
    mobile: '',
    whatsapp: '',
    email: '',
    lead_source: '',
    sub_source: '',
    referral_name: '',
    interested_brand: '',
    interested_model: '',
    interested_variant: '',
    interested_color: '',
    budget_min: '',
    budget_max: '',
    has_exchange: false,
    exchange_vehicle_make: '',
    exchange_vehicle_model: '',
    exchange_vehicle_variant: '',
    exchange_vehicle_color: '',
    exchange_vehicle_year: '',
    exchange_vehicle_reg_no: '',
    exchange_expected_price: '',
    needs_finance: false,
    preferred_financier: '',
    stage: 'new',
    priority: 'warm',
    assigned_to: '',
    lost_reason: '',
    lost_to_competitor: '',
    next_follow_up_date: '',
    next_follow_up_time: '',
    remarks: '',
    finance_status: '',
    dp_amount: '',
    dp_received: false
  });

  // Fetch masters data on mount, then fetch lead if in edit mode
  React.useEffect(() => {
    const loadData = async () => {
      const brandsData = await fetchMastersData();
      if (mode === 'edit' && id && brandsData) {
        await fetchLead(brandsData);
      }
    };
    loadData();
  }, [mode, id]);

  const fetchMastersData = async () => {
    try {
      const [brandsRes, modelsRes, variantsRes, colorsRes, salesRepsRes, financiersRes, leadSourcesRes, subSourcesRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/masters?type=brand`, getAuthHeader()),
        axios.get(`${BACKEND_URL}/api/masters?type=model`, getAuthHeader()),
        axios.get(`${BACKEND_URL}/api/masters?type=variant`, getAuthHeader()),
        axios.get(`${BACKEND_URL}/api/masters?type=color`, getAuthHeader()),
        axios.get(`${BACKEND_URL}/api/masters?type=sales_rep`, getAuthHeader()),
        axios.get(`${BACKEND_URL}/api/masters?type=financier`, getAuthHeader()),
        axios.get(`${BACKEND_URL}/api/masters?type=lead_source`, getAuthHeader()),
        axios.get(`${BACKEND_URL}/api/masters?type=sub_source`, getAuthHeader())
      ]);
      
      setBrands(brandsRes.data);
      setAllModels(modelsRes.data);
      setAllVariants(variantsRes.data);
      setAllColors(colorsRes.data);
      setSalesReps(salesRepsRes.data);
      setFinanciers(financiersRes.data);
      setLeadSources(leadSourcesRes.data);
      setSubSources(subSourcesRes.data);
      
      return brandsRes.data; // Return brands for immediate use
    } catch (error) {
      console.error('Error fetching masters:', error);
      toast.error('Failed to load master data');
      return null;
    }
  };

  // Fetch filtered models based on brand selection
  const fetchFilteredModels = async (brandId, isExchange = false) => {
    if (!brandId) {
      if (isExchange) {
        setExchangeFilteredModels([]);
        setExchangeFilteredVariants([]);
        setExchangeFilteredColors([]);
      } else {
        setFilteredModels([]);
        setFilteredVariants([]);
        setFilteredColors([]);
      }
      return;
    }
    try {
      const response = await axios.get(`${BACKEND_URL}/api/masters?type=model&parent_id=${brandId}`, getAuthHeader());
      if (isExchange) {
        setExchangeFilteredModels(response.data);
        setExchangeFilteredVariants([]);
        setExchangeFilteredColors([]);
      } else {
        setFilteredModels(response.data);
        setFilteredVariants([]);
        setFilteredColors([]);
      }
    } catch (error) {
      console.error('Error fetching filtered models:', error);
    }
  };

  // Fetch filtered variants based on model selection
  const fetchFilteredVariants = async (modelId, isExchange = false) => {
    if (!modelId) {
      if (isExchange) {
        setExchangeFilteredVariants([]);
        setExchangeFilteredColors([]);
      } else {
        setFilteredVariants([]);
        setFilteredColors([]);
      }
      return;
    }
    try {
      const response = await axios.get(`${BACKEND_URL}/api/masters?type=variant&parent_id=${modelId}`, getAuthHeader());
      if (isExchange) {
        setExchangeFilteredVariants(response.data);
        setExchangeFilteredColors([]);
      } else {
        setFilteredVariants(response.data);
        setFilteredColors([]);
      }
    } catch (error) {
      console.error('Error fetching filtered variants:', error);
    }
  };

  // Fetch filtered colors based on variant selection
  const fetchFilteredColors = async (variantId, isExchange = false) => {
    if (!variantId) {
      if (isExchange) {
        setExchangeFilteredColors([]);
      } else {
        setFilteredColors([]);
      }
      return;
    }
    try {
      const response = await axios.get(`${BACKEND_URL}/api/masters?type=color&parent_id=${variantId}`, getAuthHeader());
      if (isExchange) {
        setExchangeFilteredColors(response.data);
      } else {
        setFilteredColors(response.data);
      }
    } catch (error) {
      console.error('Error fetching filtered colors:', error);
    }
  };

  const fetchLead = async (brandsData = null) => {
    setLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/leads/${id}`, getAuthHeader());
      const leadData = response.data;
      setFormData(leadData);
      
      // Use passed brandsData or fall back to state
      const brandsToUse = brandsData || brands;
      
      // Initialize cascading data if lead has existing values
      // For interested vehicle - need to load models, then variants, then colors
      if (leadData.interested_brand && brandsToUse.length > 0) {
        const brand = brandsToUse.find(b => b.name === leadData.interested_brand);
        if (brand) {
          // Fetch models for the brand
          const modelsRes = await axios.get(`${BACKEND_URL}/api/masters?type=model&parent_id=${brand.id}`, getAuthHeader());
          setFilteredModels(modelsRes.data);
          
          // If there's a model saved, fetch variants
          if (leadData.interested_model) {
            const model = modelsRes.data.find(m => m.name === leadData.interested_model);
            if (model) {
              const variantsRes = await axios.get(`${BACKEND_URL}/api/masters?type=variant&parent_id=${model.id}`, getAuthHeader());
              setFilteredVariants(variantsRes.data);
              
              // If there's a variant saved, fetch colors
              if (leadData.interested_variant) {
                const variant = variantsRes.data.find(v => v.name === leadData.interested_variant);
                if (variant) {
                  const colorsRes = await axios.get(`${BACKEND_URL}/api/masters?type=color&parent_id=${variant.id}`, getAuthHeader());
                  setFilteredColors(colorsRes.data);
                }
              }
            }
          }
        }
      }
      
      // For exchange vehicle - same cascading logic
      if (leadData.exchange_vehicle_make && brandsToUse.length > 0) {
        const brand = brandsToUse.find(b => b.name === leadData.exchange_vehicle_make);
        if (brand) {
          // Fetch models for the exchange brand
          const modelsRes = await axios.get(`${BACKEND_URL}/api/masters?type=model&parent_id=${brand.id}`, getAuthHeader());
          setExchangeFilteredModels(modelsRes.data);
          
          // If there's a model saved, fetch variants
          if (leadData.exchange_vehicle_model) {
            const model = modelsRes.data.find(m => m.name === leadData.exchange_vehicle_model);
            if (model) {
              const variantsRes = await axios.get(`${BACKEND_URL}/api/masters?type=variant&parent_id=${model.id}`, getAuthHeader());
              setExchangeFilteredVariants(variantsRes.data);
              
              // If there's a variant saved, fetch colors
              if (leadData.exchange_vehicle_variant) {
                const variant = variantsRes.data.find(v => v.name === leadData.exchange_vehicle_variant);
                if (variant) {
                  const colorsRes = await axios.get(`${BACKEND_URL}/api/masters?type=color&parent_id=${variant.id}`, getAuthHeader());
                  setExchangeFilteredColors(colorsRes.data);
                }
              }
            }
          }
        }
      }
    } catch (error) {
      console.error('Error fetching lead:', error);
      toast.error('Failed to load lead');
      navigate(-1);
    }
    setLoading(false);
  };

  // Handle cascading brand change for interested vehicle
  const handleBrandChange = async (e) => {
    const { value } = e.target;
    const selectedBrand = brands.find(b => b.name === value);
    
    setFormData(prev => ({
      ...prev,
      interested_brand: value,
      interested_model: '',
      interested_variant: '',
      interested_color: ''
    }));
    
    await fetchFilteredModels(selectedBrand?.id, false);
  };

  // Handle cascading model change for interested vehicle
  const handleModelChange = async (e) => {
    const { value } = e.target;
    const selectedModel = filteredModels.find(m => m.name === value);
    
    setFormData(prev => ({
      ...prev,
      interested_model: value,
      interested_variant: '',
      interested_color: ''
    }));
    
    await fetchFilteredVariants(selectedModel?.id, false);
  };

  // Handle cascading variant change for interested vehicle
  const handleVariantChange = async (e) => {
    const { value } = e.target;
    const selectedVariant = filteredVariants.find(v => v.name === value);
    
    setFormData(prev => ({
      ...prev,
      interested_variant: value,
      interested_color: ''
    }));
    
    await fetchFilteredColors(selectedVariant?.id, false);
  };

  // Handle cascading brand change for exchange vehicle
  const handleExchangeBrandChange = async (e) => {
    const { value } = e.target;
    const selectedBrand = brands.find(b => b.name === value);
    
    setFormData(prev => ({
      ...prev,
      exchange_vehicle_make: value,
      exchange_vehicle_model: '',
      exchange_vehicle_variant: '',
      exchange_vehicle_color: ''
    }));
    
    await fetchFilteredModels(selectedBrand?.id, true);
  };

  // Handle cascading model change for exchange vehicle
  const handleExchangeModelChange = async (e) => {
    const { value } = e.target;
    const selectedModel = exchangeFilteredModels.find(m => m.name === value);
    
    setFormData(prev => ({
      ...prev,
      exchange_vehicle_model: value,
      exchange_vehicle_variant: '',
      exchange_vehicle_color: ''
    }));
    
    await fetchFilteredVariants(selectedModel?.id, true);
  };

  // Handle cascading variant change for exchange vehicle
  const handleExchangeVariantChange = async (e) => {
    const { value } = e.target;
    const selectedVariant = exchangeFilteredVariants.find(v => v.name === value);
    
    setFormData(prev => ({
      ...prev,
      exchange_vehicle_variant: value,
      exchange_vehicle_color: ''
    }));
    
    await fetchFilteredColors(selectedVariant?.id, true);
  };

  // Check for duplicate mobile number
  const checkDuplicateMobile = async (mobile) => {
    if (!mobile || mobile.length !== 10 || !/^[6-9][0-9]{9}$/.test(mobile)) {
      return; // Don't check invalid numbers
    }
    
    // Skip duplicate check in edit mode for the same lead
    if (mode === 'edit') {
      return;
    }
    
    setCheckingDuplicate(true);
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/leads/check-duplicate?mobile=${mobile}`,
        getAuthHeader()
      );
      
      if (response.data.has_duplicate) {
        setDuplicateLeads(response.data.existing_leads);
        setShowDuplicateModal(true);
        setDuplicateChecked(true);
      } else {
        setDuplicateLeads([]);
        setDuplicateChecked(true);
      }
    } catch (error) {
      console.error('Error checking duplicate:', error);
      // Don't block on error, just log it
    }
    setCheckingDuplicate(false);
  };

  // Handle mobile number change with duplicate check
  const handleMobileChange = (e) => {
    const { value } = e.target;
    setFormData(prev => ({
      ...prev,
      mobile: value
    }));
    
    // Reset duplicate state when mobile changes
    setDuplicateChecked(false);
    setProceedWithDuplicate(false);
    setDuplicateLeads([]);
  };

  // Handle mobile blur to trigger duplicate check
  const handleMobileBlur = () => {
    if (formData.mobile && formData.mobile.length === 10) {
      checkDuplicateMobile(formData.mobile);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // If stage is being changed to 'won', ask about conversion to booking
    if (name === 'stage' && value === 'won' && formData.stage !== 'won') {
      const shouldConvert = window.confirm(
        'Would you like to convert this lead to a booking?\n\n' +
        'Click OK to convert to booking (lead will be marked as Won)\n' +
        'Click Cancel to just mark as Won without creating a booking'
      );
      
      if (shouldConvert) {
        // Trigger conversion API
        handleConvertToBooking();
        return; // Don't update stage manually, conversion will do it
      }
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.first_name.trim()) {
      toast.error('First name is required');
      return;
    }
    if (!formData.mobile.trim()) {
      toast.error('Mobile number is required');
      return;
    }
    if (!/^[6-9][0-9]{9}$/.test(formData.mobile)) {
      toast.error('Invalid mobile number (10 digits starting with 6-9)');
      return;
    }
    
    // Check for duplicate mobile in create mode (unless user already chose to proceed)
    if (mode === 'create' && !proceedWithDuplicate && !duplicateChecked) {
      setCheckingDuplicate(true);
      try {
        const response = await axios.get(
          `${BACKEND_URL}/api/leads/check-duplicate?mobile=${formData.mobile}`,
          getAuthHeader()
        );
        
        if (response.data.has_duplicate) {
          setDuplicateLeads(response.data.existing_leads);
          setShowDuplicateModal(true);
          setCheckingDuplicate(false);
          return; // Stop submission, let user decide
        }
      } catch (error) {
        console.error('Error checking duplicate:', error);
        // Continue with submission if check fails
      }
      setCheckingDuplicate(false);
    }
    
    // If duplicates exist and user hasn't confirmed, show modal
    if (mode === 'create' && duplicateLeads.length > 0 && !proceedWithDuplicate) {
      setShowDuplicateModal(true);
      return;
    }
    
    // Clean form data - convert empty strings to null for optional fields
    const cleanedData = { ...formData };
    const numericFields = ['budget_min', 'budget_max', 'exchange_vehicle_year', 'exchange_expected_price', 'dp_amount'];
    const stringFields = ['salutation', 'last_name', 'whatsapp', 'email', 'referral_name', 
      'interested_brand', 'interested_model', 'interested_variant', 'interested_color',
      'exchange_vehicle_make', 'exchange_vehicle_model', 'exchange_vehicle_variant', 'exchange_vehicle_color', 'exchange_vehicle_reg_no',
      'preferred_financier', 'assigned_to', 'lost_reason', 'lost_to_competitor',
      'next_follow_up_date', 'next_follow_up_time', 'remarks', 'finance_status'];
    
    numericFields.forEach(field => {
      if (cleanedData[field] === '' || cleanedData[field] === null || cleanedData[field] === undefined) {
        cleanedData[field] = null;
      } else {
        cleanedData[field] = Number(cleanedData[field]);
      }
    });
    
    stringFields.forEach(field => {
      if (cleanedData[field] === '') {
        cleanedData[field] = null;
      }
    });
    
    setSaving(true);
    try {
      if (mode === 'edit') {
        await axios.put(`${BACKEND_URL}/api/leads/${id}`, cleanedData, getAuthHeader());
        toast.success('Lead updated successfully!');
      } else {
        await axios.post(`${BACKEND_URL}/api/leads`, cleanedData, getAuthHeader());
        toast.success('Lead created successfully!');
      }
      navigate(-1);
    } catch (error) {
      console.error('Error saving lead:', error);
      // Handle Pydantic validation errors which return an array
      const errorDetail = error.response?.data?.detail;
      let errorMessage = 'Failed to save lead';
      if (typeof errorDetail === 'string') {
        errorMessage = errorDetail;
      } else if (Array.isArray(errorDetail)) {
        // Extract first validation error message
        errorMessage = errorDetail[0]?.msg || errorDetail[0]?.message || 'Validation error';
      } else if (errorDetail?.msg) {
        errorMessage = errorDetail.msg;
      }
      toast.error(errorMessage);
    }
    setSaving(false);
  };

  const handleConvertToBooking = async () => {
    if (!window.confirm('Convert this lead to a booking? This will mark the lead as WON and create a new booking in Sales.')) {
      return;
    }
    
    setSaving(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/leads/${id}/convert`, {}, getAuthHeader());
      toast.success(`Lead converted! Booking #${response.data.booking?.booking_no} created.`);
      navigate(-1);
    } catch (error) {
      console.error('Error converting lead:', error);
      const errorDetail = error.response?.data?.detail;
      let errorMessage = 'Failed to convert lead';
      if (typeof errorDetail === 'string') {
        errorMessage = errorDetail;
      } else if (Array.isArray(errorDetail)) {
        errorMessage = errorDetail[0]?.msg || errorDetail[0]?.message || 'Validation error';
      } else if (errorDetail?.msg) {
        errorMessage = errorDetail.msg;
      }
      toast.error(errorMessage);
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <LeadsLayout user={user} onLogout={onLogout} title={mode === 'edit' ? 'Edit Lead' : 'Add Lead'}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
        </div>
      </LeadsLayout>
    );
  }

  return (
    <LeadsLayout user={user} onLogout={onLogout} title={mode === 'edit' ? 'Edit Lead' : 'Add New Lead'}>
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Header with actions */}
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold text-gray-800" data-testid="lead-form-title">
              {mode === 'edit' ? `Edit Lead #${formData.lead_no || ''}` : 'New Lead Entry'}
            </h2>
            <p className="text-sm text-gray-500">Fill in the lead details below</p>
          </div>
          <div className="flex gap-3">
            {mode === 'edit' && formData.stage !== 'won' && formData.stage !== 'lost' && (
              <button
                type="button"
                onClick={handleConvertToBooking}
                disabled={saving}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 font-medium"
                data-testid="convert-to-booking-btn"
              >
                🎉 Convert to Booking
              </button>
            )}
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-6 py-2 text-white rounded-xl font-medium shadow-[0_4px_12px_rgba(5,150,105,0.30)] hover:shadow-[0_8px_20px_rgba(5,150,105,0.40)] transition-all duration-180 ease-out hover:scale-[1.02] active:scale-95 disabled:opacity-50 disabled:scale-100"
              style={{ background: 'linear-gradient(135deg, #059669 0%, #0D9488 100%)' }}
              data-testid="save-lead-btn"
            >
              {saving ? 'Saving...' : mode === 'edit' ? 'Update Lead' : 'Save Lead'}
            </button>
          </div>
        </div>

        {/* Lead Status Bar (Edit mode) */}
        {mode === 'edit' && (
          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
            <div className="flex items-center gap-6 flex-wrap">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Stage</label>
                <select
                  name="stage"
                  value={formData.stage}
                  onChange={handleChange}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  data-testid="lead-stage-select"
                >
                  {STAGES.map(s => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                <div className="flex gap-2">
                  {PRIORITIES.map(p => (
                    <button
                      key={p.value}
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, priority: p.value }))}
                      className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                        formData.priority === p.value
                          ? `${p.color} text-white shadow-md`
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                      data-testid={`priority-${p.value}-btn`}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">Assigned To</label>
                <select
                  name="assigned_to"
                  value={formData.assigned_to}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  data-testid="assigned-to-select"
                >
                  <option value="">Unassigned</option>
                  {salesReps.map(rep => (
                    <option key={rep.id} value={rep.name}>{rep.name}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        )}

        {/* Finance Approval Status (Show when stage is finance_pending) */}
        {mode === 'edit' && formData.stage === 'finance_pending' && (
          <div className="bg-orange-50 rounded-xl p-6 shadow-sm border border-orange-200">
            <h3 className="text-lg font-bold text-orange-800 mb-4 flex items-center gap-2">
              <span className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center text-orange-600">🏦</span>
              Finance Approval Status
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-orange-700 mb-1">Financier</label>
                <p className="px-3 py-2 bg-white border border-orange-300 rounded-lg text-gray-800 font-medium">
                  {formData.preferred_financier || 'Not Selected'}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-orange-700 mb-1">Approval Status *</label>
                <select
                  name="finance_status"
                  value={formData.finance_status || 'fi_pending'}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-orange-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 bg-white"
                  data-testid="finance-status-select"
                >
                  {FINANCE_STATUS_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-end">
                {formData.finance_status === 'disbursement_done' && (
                  <div className="px-4 py-2 bg-green-100 text-green-700 rounded-lg font-medium text-sm flex items-center gap-2">
                    ✅ Ready for Booking Conversion
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* DP Pending Status (Show when stage is dp_pending) */}
        {mode === 'edit' && formData.stage === 'dp_pending' && (
          <div className="bg-amber-50 rounded-xl p-6 shadow-sm border border-amber-200">
            <h3 className="text-lg font-bold text-amber-800 mb-4 flex items-center gap-2">
              <span className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center text-amber-600">💰</span>
              Down Payment Status
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-amber-700 mb-1">DP Amount (₹)</label>
                <input
                  type="number"
                  name="dp_amount"
                  value={formData.dp_amount}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-amber-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                  placeholder="Enter down payment amount"
                  data-testid="dp-amount-input"
                />
              </div>
              <div className="flex items-center">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    name="dp_received"
                    checked={formData.dp_received}
                    onChange={handleChange}
                    className="w-5 h-5 text-amber-600 rounded focus:ring-amber-500"
                    data-testid="dp-received-checkbox"
                  />
                  <span className="text-sm font-medium text-amber-700">Down Payment Received</span>
                </label>
              </div>
              <div className="flex items-end">
                {formData.dp_received && (
                  <div className="px-4 py-2 bg-green-100 text-green-700 rounded-lg font-medium text-sm flex items-center gap-2">
                    ✅ DP Collected - Ready to Proceed
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Customer Details */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
            <span className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center text-green-600">👤</span>
            Customer Details
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Salutation</label>
              <select
                name="salutation"
                value={formData.salutation}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                data-testid="salutation-select"
              >
                <option value="">Select</option>
                {SALUTATIONS.map(s => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
              <input
                type="text"
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                placeholder="First name"
                data-testid="first-name-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
              <input
                type="text"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                placeholder="Last name"
                data-testid="last-name-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Mobile *</label>
              <div className="relative">
                <input
                  type="tel"
                  name="mobile"
                  value={formData.mobile}
                  onChange={handleMobileChange}
                  onBlur={handleMobileBlur}
                  required
                  maxLength={10}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 ${
                    duplicateLeads.length > 0 && !proceedWithDuplicate
                      ? 'border-orange-400 bg-orange-50'
                      : 'border-gray-300'
                  }`}
                  placeholder="10-digit mobile"
                  data-testid="mobile-input"
                />
                {checkingDuplicate && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <div className="animate-spin h-4 w-4 border-2 border-emerald-500 rounded-full border-t-transparent"></div>
                  </div>
                )}
                {duplicateLeads.length > 0 && !proceedWithDuplicate && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <button
                      type="button"
                      onClick={() => setShowDuplicateModal(true)}
                      className="text-orange-600 hover:text-orange-800"
                      title="Duplicate found - Click to view"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                    </button>
                  </div>
                )}
              </div>
              {duplicateLeads.length > 0 && !proceedWithDuplicate && (
                <p className="mt-1 text-xs text-orange-600">
                  ⚠️ {duplicateLeads.length} existing lead(s) found with this mobile.{' '}
                  <button
                    type="button"
                    onClick={() => setShowDuplicateModal(true)}
                    className="underline hover:text-orange-800"
                  >
                    View details
                  </button>
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">WhatsApp</label>
              <input
                type="tel"
                name="whatsapp"
                value={formData.whatsapp}
                onChange={handleChange}
                maxLength={10}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                placeholder="WhatsApp number"
                data-testid="whatsapp-input"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                placeholder="Email address"
                data-testid="email-input"
              />
            </div>
          </div>
        </div>

        {/* Lead Source & Priority (Create mode) */}
        {mode === 'create' && (
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
              <span className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-600">📊</span>
              Lead Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Lead Source *</label>
                <select
                  name="lead_source"
                  value={formData.lead_source}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  data-testid="lead-source-select"
                >
                  <option value="">Select Lead Source</option>
                  {leadSources.length > 0 ? (
                    leadSources.map(src => (
                      <option key={src.id} value={src.name}>{src.name}</option>
                    ))
                  ) : (
                    LEAD_SOURCES.map(src => (
                      <option key={src} value={src}>{src}</option>
                    ))
                  )}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Sub Source</label>
                <select
                  name="sub_source"
                  value={formData.sub_source}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  data-testid="sub-source-select"
                >
                  <option value="">Select Sub Source</option>
                  {subSources.map(src => (
                    <option key={src.id} value={src.name}>{src.name}</option>
                  ))}
                </select>
              </div>
              {formData.lead_source === 'Referral' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Referral Name</label>
                  <input
                    type="text"
                    name="referral_name"
                    value={formData.referral_name}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    placeholder="Who referred?"
                    data-testid="referral-name-input"
                  />
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                <div className="flex gap-2">
                  {PRIORITIES.map(p => (
                    <button
                      key={p.value}
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, priority: p.value }))}
                      className={`px-3 py-2 rounded-lg text-sm font-medium transition-all flex-1 ${
                        formData.priority === p.value
                          ? `${p.color} text-white shadow-md`
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                      data-testid={`priority-${p.value}-btn`}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Sales Representative *</label>
                <select
                  name="assigned_to"
                  value={formData.assigned_to}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  data-testid="assigned-to-select"
                >
                  <option value="">Select Sales Rep</option>
                  {salesReps.map(rep => (
                    <option key={rep.id} value={rep.name}>{rep.name}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        )}

        {/* Vehicle Interest */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
            <span className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center text-purple-600">🏍️</span>
            Vehicle Interest
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Brand</label>
              <select
                name="interested_brand"
                value={formData.interested_brand}
                onChange={handleBrandChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                data-testid="brand-select"
              >
                <option value="">Select Brand</option>
                {brands.map(b => (
                  <option key={b.id} value={b.name}>{b.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Model</label>
              <select
                name="interested_model"
                value={formData.interested_model}
                onChange={handleModelChange}
                disabled={!formData.interested_brand}
                className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 ${!formData.interested_brand ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                data-testid="model-select"
              >
                <option value="">{formData.interested_brand ? 'Select Model' : 'Select Brand first'}</option>
                {/* Include saved value if not in filtered list */}
                {formData.interested_model && !filteredModels.some(m => m.name === formData.interested_model) && (
                  <option value={formData.interested_model}>{formData.interested_model} (saved)</option>
                )}
                {filteredModels.map(m => (
                  <option key={m.id} value={m.name}>{m.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Variant</label>
              <select
                name="interested_variant"
                value={formData.interested_variant}
                onChange={handleVariantChange}
                disabled={!formData.interested_model}
                className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 ${!formData.interested_model ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                data-testid="variant-select"
              >
                <option value="">{formData.interested_model ? 'Select Variant' : 'Select Model first'}</option>
                {/* Include saved value if not in filtered list */}
                {formData.interested_variant && !filteredVariants.some(v => v.name === formData.interested_variant) && (
                  <option value={formData.interested_variant}>{formData.interested_variant} (saved)</option>
                )}
                {filteredVariants.map(v => (
                  <option key={v.id} value={v.name}>{v.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Color</label>
              <select
                name="interested_color"
                value={formData.interested_color}
                onChange={handleChange}
                disabled={!formData.interested_variant}
                className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 ${!formData.interested_variant ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                data-testid="color-select"
              >
                <option value="">{formData.interested_variant ? 'Select Color' : 'Select Variant first'}</option>
                {/* Include saved value if not in filtered list */}
                {formData.interested_color && !filteredColors.some(c => c.name === formData.interested_color) && (
                  <option value={formData.interested_color}>{formData.interested_color} (saved)</option>
                )}
                {filteredColors.map(c => (
                  <option key={c.id} value={c.name}>{c.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Budget Min (₹)</label>
              <input
                type="number"
                name="budget_min"
                value={formData.budget_min}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                placeholder="Min budget"
                data-testid="budget-min-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Budget Max (₹)</label>
              <input
                type="number"
                name="budget_max"
                value={formData.budget_max}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                placeholder="Max budget"
                data-testid="budget-max-input"
              />
            </div>
          </div>
        </div>

        {/* Exchange Vehicle */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
              <span className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center text-orange-600">🔄</span>
              Exchange Vehicle
            </h3>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                name="has_exchange"
                checked={formData.has_exchange}
                onChange={handleChange}
                className="w-5 h-5 text-emerald-600 rounded focus:ring-emerald-500"
                data-testid="has-exchange-checkbox"
              />
              <span className="text-sm font-medium text-gray-700">Has Exchange Vehicle</span>
            </label>
          </div>
          
          {formData.has_exchange && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Make (Brand)</label>
                <select
                  name="exchange_vehicle_make"
                  value={formData.exchange_vehicle_make}
                  onChange={handleExchangeBrandChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  data-testid="exchange-make-select"
                >
                  <option value="">Select Make</option>
                  {brands.map(b => (
                    <option key={b.id} value={b.name}>{b.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Model</label>
                <select
                  name="exchange_vehicle_model"
                  value={formData.exchange_vehicle_model}
                  onChange={handleExchangeModelChange}
                  disabled={!formData.exchange_vehicle_make}
                  className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 ${!formData.exchange_vehicle_make ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                  data-testid="exchange-model-select"
                >
                  <option value="">{formData.exchange_vehicle_make ? 'Select Model' : 'Select Make first'}</option>
                  {/* Include saved value if not in filtered list */}
                  {formData.exchange_vehicle_model && !exchangeFilteredModels.some(m => m.name === formData.exchange_vehicle_model) && (
                    <option value={formData.exchange_vehicle_model}>{formData.exchange_vehicle_model} (saved)</option>
                  )}
                  {exchangeFilteredModels.map(m => (
                    <option key={m.id} value={m.name}>{m.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Variant</label>
                <select
                  name="exchange_vehicle_variant"
                  value={formData.exchange_vehicle_variant}
                  onChange={handleExchangeVariantChange}
                  disabled={!formData.exchange_vehicle_model}
                  className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 ${!formData.exchange_vehicle_model ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                  data-testid="exchange-variant-select"
                >
                  <option value="">{formData.exchange_vehicle_model ? 'Select Variant' : 'Select Model first'}</option>
                  {/* Include saved value if not in filtered list */}
                  {formData.exchange_vehicle_variant && !exchangeFilteredVariants.some(v => v.name === formData.exchange_vehicle_variant) && (
                    <option value={formData.exchange_vehicle_variant}>{formData.exchange_vehicle_variant} (saved)</option>
                  )}
                  {exchangeFilteredVariants.map(v => (
                    <option key={v.id} value={v.name}>{v.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Color</label>
                <select
                  name="exchange_vehicle_color"
                  value={formData.exchange_vehicle_color}
                  onChange={handleChange}
                  disabled={!formData.exchange_vehicle_variant}
                  className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 ${!formData.exchange_vehicle_variant ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                  data-testid="exchange-color-select"
                >
                  <option value="">{formData.exchange_vehicle_variant ? 'Select Color' : 'Select Variant first'}</option>
                  {/* Include saved value if not in filtered list */}
                  {formData.exchange_vehicle_color && !exchangeFilteredColors.some(c => c.name === formData.exchange_vehicle_color) && (
                    <option value={formData.exchange_vehicle_color}>{formData.exchange_vehicle_color} (saved)</option>
                  )}
                  {exchangeFilteredColors.map(c => (
                    <option key={c.id} value={c.name}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
                <input
                  type="number"
                  name="exchange_vehicle_year"
                  value={formData.exchange_vehicle_year}
                  onChange={handleChange}
                  min="1990"
                  max={new Date().getFullYear()}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  placeholder="Year"
                  data-testid="exchange-year-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Registration No</label>
                <input
                  type="text"
                  name="exchange_vehicle_reg_no"
                  value={formData.exchange_vehicle_reg_no}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  placeholder="AP XX XX XXXX"
                  data-testid="exchange-regno-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Expected Price (₹)</label>
                <input
                  type="number"
                  name="exchange_expected_price"
                  value={formData.exchange_expected_price}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  placeholder="Expected exchange value"
                  data-testid="exchange-price-input"
                />
              </div>
            </div>
          )}
        </div>

        {/* Finance Requirement */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
              <span className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center text-green-600">💰</span>
              Finance Requirement
            </h3>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                name="needs_finance"
                checked={formData.needs_finance}
                onChange={handleChange}
                className="w-5 h-5 text-emerald-600 rounded focus:ring-emerald-500"
                data-testid="needs-finance-checkbox"
              />
              <span className="text-sm font-medium text-gray-700">Needs Finance</span>
            </label>
          </div>
          
          {formData.needs_finance && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Preferred Financier</label>
                <select
                  name="preferred_financier"
                  value={formData.preferred_financier}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  data-testid="financier-select"
                >
                  <option value="">Select Financier</option>
                  {financiers.map(f => (
                    <option key={f.id} value={f.name}>{f.name}</option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </div>

        {/* Follow-up & Remarks */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
            <span className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center text-yellow-600">📅</span>
            Follow-up & Remarks
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Next Follow-up Date</label>
              <input
                type="date"
                name="next_follow_up_date"
                value={formData.next_follow_up_date}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                data-testid="followup-date-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Follow-up Time</label>
              <input
                type="time"
                name="next_follow_up_time"
                value={formData.next_follow_up_time}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                data-testid="followup-time-input"
              />
            </div>
            <div className="md:col-span-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">Remarks</label>
              <textarea
                name="remarks"
                value={formData.remarks}
                onChange={handleChange}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                placeholder="Any additional notes about this lead..."
                data-testid="remarks-textarea"
              />
            </div>
          </div>
        </div>

        {/* Lost Reason (only for lost stage) */}
        {formData.stage === 'lost' && (
          <div className="bg-red-50 rounded-xl p-6 shadow-sm border border-red-200">
            <h3 className="text-lg font-bold text-red-800 mb-4 flex items-center gap-2">
              <span className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center text-red-600">❌</span>
              Lost Lead Details
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-red-700 mb-1">Reason for Loss</label>
                <select
                  name="lost_reason"
                  value={formData.lost_reason}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-red-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  data-testid="lost-reason-select"
                >
                  <option value="">Select Reason</option>
                  <option value="Price too high">Price too high</option>
                  <option value="Bought from competitor">Bought from competitor</option>
                  <option value="Changed mind">Changed mind</option>
                  <option value="Finance not approved">Finance not approved</option>
                  <option value="Model not available">Model not available</option>
                  <option value="Poor service experience">Poor service experience</option>
                  <option value="Not interested anymore">Not interested anymore</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-red-700 mb-1">Lost to Competitor</label>
                <input
                  type="text"
                  name="lost_to_competitor"
                  value={formData.lost_to_competitor}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-red-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  placeholder="Which competitor?"
                  data-testid="lost-competitor-input"
                />
              </div>
            </div>
          </div>
        )}

        {/* Submit Button */}
        <div className="flex justify-end gap-4">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving || checkingDuplicate}
            className="px-8 py-3 text-white rounded-xl font-medium shadow-[0_8px_24px_rgba(5,150,105,0.35)] hover:shadow-[0_12px_32px_rgba(5,150,105,0.50)] transition-all duration-180 ease-out hover:scale-[1.02] active:scale-95 disabled:opacity-50 disabled:scale-100"
            style={{ background: 'linear-gradient(135deg, #059669 0%, #0D9488 60%, #0891B2 100%)' }}
            data-testid="submit-lead-btn"
          >
            {saving ? 'Saving...' : checkingDuplicate ? 'Checking...' : mode === 'edit' ? 'Update Lead' : 'Create Lead'}
          </button>
        </div>
      </form>

      {/* Duplicate Mobile Number Modal */}
      {showDuplicateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
            {/* Header */}
            <div className="p-4 bg-orange-50 border-b border-orange-200">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-orange-100 rounded-full">
                  <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-orange-800">Duplicate Mobile Number Found</h3>
                  <p className="text-sm text-orange-600">
                    {duplicateLeads.length} existing lead(s) with mobile <span className="font-mono font-bold">{formData.mobile}</span>
                  </p>
                </div>
              </div>
            </div>

            {/* Existing Leads List */}
            <div className="p-4 max-h-[400px] overflow-y-auto">
              <p className="text-sm text-gray-600 mb-3">
                The following leads already exist with this mobile number. You can view an existing lead or continue creating a new one.
              </p>
              
              <div className="space-y-3">
                {duplicateLeads.map((lead) => (
                  <div
                    key={lead.id}
                    className="border border-gray-200 rounded-lg p-3 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-gray-800">
                            {lead.first_name} {lead.last_name || ''}
                          </span>
                          <span className="text-xs text-gray-500">#{lead.lead_no}</span>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STAGE_COLORS[lead.stage] || 'bg-gray-100 text-gray-700'}`}>
                            {lead.stage?.replace(/_/g, ' ').toUpperCase()}
                          </span>
                        </div>
                        <div className="mt-1 text-sm text-gray-600 space-y-0.5">
                          <p>📱 {lead.mobile} {lead.whatsapp && lead.whatsapp !== lead.mobile && `/ WhatsApp: ${lead.whatsapp}`}</p>
                          {lead.interested_model && <p>🚗 Interested in: {lead.interested_model}</p>}
                          {lead.assigned_to && <p>👤 Assigned to: {lead.assigned_to}</p>}
                          {lead.created_at && (
                            <p className="text-xs text-gray-400">
                              Created: {new Date(lead.created_at).toLocaleDateString('en-GB')}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2 ml-3">
                        <button
                          type="button"
                          onClick={() => {
                            setShowDuplicateModal(false);
                            navigate(`/leads/view/${lead.id}`);
                          }}
                          className="px-3 py-1.5 text-sm bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 font-medium"
                        >
                          View
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setShowDuplicateModal(false);
                            navigate(`/leads/edit/${lead.id}`);
                          }}
                          className="px-3 py-1.5 text-sm bg-green-100 text-green-700 rounded-lg hover:bg-green-200 font-medium"
                        >
                          Edit
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Actions */}
            <div className="p-4 bg-gray-50 border-t border-gray-200 flex justify-between items-center">
              <button
                type="button"
                onClick={() => {
                  setShowDuplicateModal(false);
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  setProceedWithDuplicate(true);
                  setShowDuplicateModal(false);
                  // Re-submit the form after a short delay
                  setTimeout(() => {
                    document.querySelector('[data-testid="submit-lead-btn"]')?.click();
                  }, 100);
                }}
                className="px-6 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 font-medium"
              >
                Create New Lead Anyway
              </button>
            </div>
          </div>
        </div>
      )}
    </LeadsLayout>
  );
}

export default LeadEntryForm;
